/**
 * Gates money-committing actions (Add to Cart / Update Cart) on the product
 * details page while trade-agreement price refreshes are in flight (#111920).
 *
 * addToCart() submits client-computed subtotal/total derived from the
 * displayed unit price, and the server persists them as-is — so committing
 * while a price refresh is pending can store a stale price. The full-screen
 * loading overlay used to prevent this as a side effect; with the overlay
 * suppressed for background refreshes, this gate enforces it explicitly.
 *
 * settle() must be called from BOTH success and failure paths — a gate that
 * never settles leaves Add to Cart disabled permanently. Settling the same
 * ticket twice is safe (a success callback that throws is routed into the
 * failure callback by the shared http mixin, settling again).
 */
function createPriceRefreshGate() {
    let lastTicket = 0;
    const pending = new Set();

    return {
        begin() {
            const ticket = ++lastTicket;
            pending.add(ticket);
            return ticket;
        },
        /**
         * Releases the ticket. Returns true only when no newer begin() happened
         * since — responses apply in arrival order, so a slow response for an
         * older quantity can land last; callers must discard it (return false).
         */
        settle(ticket) {
            pending.delete(ticket);
            if (ticket === lastTicket) {
                // The latest request has concluded, so any older request still in
                // flight is obsolete — its response will be discarded above — and
                // must not keep holding the gate: a hung request with no timeout
                // would otherwise leave Add to Cart disabled indefinitely.
                pending.clear();
                return true;
            }
            return false;
        },
        refreshing() {
            return pending.size > 0;
        },
    };
}

/**
 * Coalesces rapid quantity taps into one price reload (#111920). The gate goes
 * up on the FIRST tap — the displayed price stops matching the chosen quantity
 * the moment the user taps, not when the debounced request finally fires — and
 * is handed over to the reload's own ticket when the timer fires.
 */
function createQuantityReloadQueue({ begin, settle, reload, delayMs }) {
    let timer = null;
    let ticket = null;

    return {
        tap() {
            if (timer) {
                clearTimeout(timer);
            } else {
                ticket = begin();
            }
            timer = setTimeout(() => {
                timer = null;
                settle(ticket);
                ticket = null;
                reload();
            }, delayMs);
        },
        /**
         * Drops a pending reload without firing it and releases its held ticket —
         * for when the owner no longer wants a price for the product that was tapped
         * (Quick Add modal closed or switched to another product). No-op when idle.
         */
        cancel() {
            if (!timer) {
                return false;
            }
            clearTimeout(timer);
            timer = null;
            settle(ticket);
            ticket = null;
            return true;
        },
    };
}

module.exports = { createPriceRefreshGate, createQuantityReloadQueue };
